/*
  Traceform round-1 storefront funnel (Shopify theme asset). Preview-first:
  upload a Strava share-card image OR a GPX export (config/round1.json declares
  both; `partFor` decides which part the engine is sent) -> POST preview
  (watermarked image + opaque X-Traceform-Entitlement
  header) -> choose 18x24 print/framed (+ optional digital) -> POST /cart/add.js
  carrying ONLY the opaque entitlement as a private "_entitlement" line
  property -> Shopify checkout. No full-resolution URL or path is ever placed in
  the DOM or a request body. Config (endpoints + variant ids) is injected by
  Liquid via the #traceform-config JSON block. Contract: config/round1.json.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-testid="traceform-funnel"]');
  if (!root) return;

  var cfgEl = document.getElementById('traceform-config');
  var cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent);
  } catch (err) {
    return;
  }

  var variantsByKey = {};
  (cfg.variants || []).forEach(function (v) {
    variantsByKey[v.key] = v;
  });
  var digital = cfg.digital || null;
  // `copy` also carries the existing localized UI strings; only an id enables the add-on.
  var copyAddon = cfg.copy && cfg.copy.id ? cfg.copy : null;

  function $(sel) {
    return root.querySelector(sel);
  }
  function testid(id) {
    return root.querySelector('[data-testid="' + id + '"]');
  }

  var heroZoom = testid('hero-zoom');
  var heroDialog = testid('hero-zoom-dialog');
  if (heroZoom && heroDialog) {
    var bodyOverflow;
    heroZoom.addEventListener('click', function () {
      bodyOverflow = document.body.style.overflow;
      heroDialog.showModal();
      document.body.style.overflow = 'hidden';
    });
    testid('hero-zoom-close').addEventListener('click', function () {
      heroDialog.close();
    });
    heroDialog.addEventListener('click', function (event) {
      var box = heroDialog.getBoundingClientRect();
      if (event.target === heroDialog &&
          (event.clientX < box.left || event.clientX > box.right ||
           event.clientY < box.top || event.clientY > box.bottom)) heroDialog.close();
    });
    heroDialog.addEventListener('close', function () {
      document.body.style.overflow = bodyOverflow;
      heroZoom.focus({ preventScroll: true });
    });
  }

  var fileInput = testid('gpx-input');
  var dropzone = testid('dropzone');
  var fileName = testid('file-name');
  var retryBtn = testid('preview-retry');
  var tuningPanel = testid('tuning-panel');
  var previewBox = testid('preview');
  var previewImg = $('#traceform-preview-img');
  var previewHint = testid('preview-hint');
  var addBtn = testid('add-to-cart');
  var digitalBox = testid('digital-addon');
  var copyBox = testid('copy-addon');

  var errUpload = testid('error-upload');
  var errPreview = testid('error-preview');
  var errCart = testid('error-cart');
  var captionInput = testid('caption-input');
  var errCaption = testid('error-caption');
  var storyInput = testid('story-input');
  var errStory = testid('error-story');
  var noteField = testid('note-field');
  var noteInput = testid('note-input');
  var errNote = testid('error-note');
  var progress = testid('preview-progress');

  var styleInputs = root.querySelectorAll('input[name="traceform-style"]');
  var variantInputs = root.querySelectorAll('input[name="traceform-variant"]');
  var audienceInputs = root.querySelectorAll('input[name="traceform-audience"]');
  var finishLater = testid('finish-later');
  var giftDoor = testid('gift-door');
  var previewEmail = testid('preview-email');
  // The file whose preview last landed; the preview email is offered for it only.
  var previewedFile = null;
  var cardNumbers = testid('card-numbers');
  var statedNumbers = null;
  // Plan 76 T2: the numbers the last photo's sheet was drawn from, for "Fix a number".
  var cardReadout = testid('card-readout');
  var readNumbers = null;
  var rescue = testid('rescue');
  var rescueGeneration = 0;
  var uploadStep = document.getElementById('traceform-upload-step');
  // Plan 72 T3: shown once a run restored after a reload has rendered again.
  var welcomeBack = testid('welcome-back');
  var restoredFile = null;
  // The run kept for a reload, and when that file first rendered.
  var kept = { file: null, since: 0 };
  // The demo ships all five colorways and base.css reveals the checked one, so
  // switching a finish needs no JS at all. These lookups only exist so a markup
  // variant that ships a single <img> still switches.
  var demoStage = document.getElementById('traceform-demo-stage');
  var demoImg = document.getElementById('traceform-demo-img');
  var gpxHelp = testid('gpx-help');

  var token = null; // opaque signed entitlement handle from the preview endpoint
  var previewUrl = null;
  var previewBlob = null;
  var previewAt = 0;
  var checkoutRestored = false;

  /* The chosen file is READ AT ACTION TIME, never cached in module state.
     A back-navigation from checkout re-runs this script against a form the
     browser has already restored, and restoring a field does not fire `change`.
     A cached reference therefore stayed null while the customer was looking at
     their own filename, but the preview path answered as if no file were chosen
     — a dead end with no instruction anywhere on the page, escapable only by
     a manual reload. Restored files now render automatically (plan 57 T5). Measured on WebKit (Safari, i.e.
     every iPhone) where the field and its File both come back; Chromium drops
     the field entirely, which is why only one engine ever showed it. */
  function currentFile() {
    return fileInput.files && fileInput.files[0] ? fileInput.files[0] : null;
  }

  /* Two intakes, one control. `config/round1.json` declares both and the engine
     implements both; the store offered only the GPX, which is the slow half —
     exporting a file is a device switch, while a share-card image is two
     taps on the phone the ad was seen on.

     The part name is what tells the engine which reader to use, so it is derived
     from the file rather than fixed: an image posted under `gpx` is parsed as XML
     and rejected, and vice versa. Phone galleries can omit the extension and share
     sheets can supply a generic media type. Read the signature first, then use
     the extension and media type only when the bytes do not identify the input. */
  var IMAGE_RE = /\.(png|jpe?g)$/i;
  var GPX_RE = /\.gpx$/i;

  async function partFor(file) {
    var bytes;
    try {
      bytes = new Uint8Array(await file.slice(0, 16).arrayBuffer());
    } catch (dead) {
      throw new Error('file_gone');
    }
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image';
    var png = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
    if (png.every(function (byte, i) { return bytes[i] === byte; })) return 'image';
    if (/^\s*</.test(new TextDecoder('utf-8').decode(bytes))) return 'gpx';
    if (GPX_RE.test(file.name)) return 'gpx';
    if (IMAGE_RE.test(file.name)) return 'image';
    if (/^image\/(png|jpeg)$/i.test(file.type)) return 'image';
    if (/^(application\/(gpx\+xml|xml)|text\/xml)$/i.test(file.type)) return 'gpx';
    return null;
  }

  /* Render sequencing. `buildPreview()` is re-entrant — a finish chip, a format
     radio and file selection all call it, and the design invites switching — so two
     renders are routinely in flight at once. They resolve in whatever order the
     network gives them, and every one of them writes the SAME three pieces of
     shared state: `token`, the preview image, and the add-to-cart's enabled
     flag. Last writer won.

     That is not a cosmetic race. The entitlement is sealed server-side to the
     variant it was rendered for, so a late response from a superseded render
     leaves the cart holding a token for `tf-print-18x24` while the line, the
     radio and the image all say framed. The order is taken, paid, and then
     rejected by fulfilment as WRONG_VARIANT: money in, nothing out.

     Every render carries a monotonic id. It is the newest only while
     `seq === renderSeq`; the moment it is not, it must write nothing, say
     nothing and count for nothing. The abort is the cheap half (it stops a
     request whose answer is already worthless); the id is the half that is
     actually load-bearing, because a response can arrive and start being read
     before the next render is even issued. */
  var renderSeq = 0;
  var inFlight = null;
  /* What the in-flight render is OF. Duplicate change events or a double-tapped retry
     can issue two identical renders and abort the first one. The old field-blur
     and explicit-preview sequence did exactly that; double taps are ordinary
     on a phone.
     Compared against the render already running, never against the last finished
     one, so a retry after a failure or a completed render always proceeds. */
  var inFlightSignature = null;
  /* Whether the customer has ever been shown their own render. Not `token`, which
     a failed re-render clears, and not `is-unlocked`, which is layout: this is the
     question "is there something on screen that could now be out of sync?" */
  var hasPreviewed = false;
  var previewAttempted = false;

  /* Busy state on the control the customer is actually looking at. The only
     indicator used to be in the upload section — measured off-screen during a
     finish change farther down the page — so the purchase
     control just went inert for seconds with nothing to explain it. */
  var copy = cfg.copy || {};
  var buying = false;
  var navigating = false;

  function buyLabel() {
    var price = selectedVariant().price + (digital && digitalBox && digitalBox.checked ? digital.price : 0) + (copyAddon && copyBox && copyBox.checked ? copyAddon.price : 0);
    return (copy.buy || 'Buy – {price}').replace('{price}', money(price));
  }

  function setAddToCartBusy(busy) {
    if (busy) {
      addBtn.setAttribute('aria-busy', 'true');
      addBtn.textContent = buying ? (copy.buyBusy || 'Opening checkout…') : (copy.addToCartBusy || 'Updating your preview…');
    } else {
      addBtn.removeAttribute('aria-busy');
      addBtn.textContent = buyLabel();
    }
  }

  /* Funnel instrumentation (plan 21 T6). Round 1 shipped with zero telemetry, so
     no funnel claim could be attributed to the offer vs. the store.
     Payloads are ALLOWLISTED, never denylisted: a GPX filename, file bytes, a
     coordinate or the entitlement token must not reach an analytics sink even by
     accident. Any key outside ALLOWED_KEYS is dropped. */
  // `retried` (boolean) joins the list for plan 23 T18: without it the retry a
  // customer silently sat through is invisible, and a preview_failure that only
  // happened on the second attempt looks identical to one that never had a
  // chance. A key absent from this list is DROPPED, not passed through.
  var ALLOWED_KEYS = [
    'style',
    'variant',
    'digital',
    'reason',
    'ms',
    'size_bucket',
    'source',
    'retried',
    'audience',
    // Plan 61 T2: a hash of the uploaded file, so it names the upload and nothing
    // in it. The pixel lifts it into Meta's `eventID` and drops it from the data.
    'event_id',
    // Plan 72 T2: `iab`, `safari` or `other` -- a bucket, never the user agent.
    'browser',
  ];

  function sanitize(payload) {
    var clean = {};
    if (!payload) return clean;
    for (var i = 0; i < ALLOWED_KEYS.length; i++) {
      var key = ALLOWED_KEYS[i];
      var value = payload[key];
      if (value === undefined || value === null) continue;
      if (typeof value === 'number' || typeof value === 'boolean') {
        clean[key] = value;
      } else {
        clean[key] = String(value).slice(0, 40);
      }
    }
    return clean;
  }

  function sizeBucket(bytes) {
    if (!bytes) return 'unknown';
    if (bytes < 102400) return 'lt100kb';
    if (bytes < 512000) return 'lt500kb';
    if (bytes < 2097152) return 'lt2mb';
    return 'gte2mb';
  }

  /* No `fbq` sink here, on purpose (plan 61 T2). Meta hears this funnel through one
     sender: the sandboxed custom pixel (`pixels/traceform-preview-pixel.js`), which
     subscribes to what `Shopify.analytics.publish` below sends and adds the standard
     `CustomizeProduct` name. A direct `fbq` call from the theme was a second sender
     with no event id -- on any page where both existed, every preview counted twice
     in the number the ad set bids on. */
  /* Plan 72 T2. Where the visit is happening, as one of three buckets: the ad click
     lands in Instagram's or Facebook's in-app browser, and that is where checkout is
     suspected to lose Apple Pay. Chrome and Firefox on iOS also say "Safari", so
     Safari means "Safari and no other browser's marker". `traceform-race-course.js`
     carries an identical copy (a separate page); `e2e/instrumentation.spec.ts`
     compares the two. */
  function browserKind() {
    var ua = (window.navigator && window.navigator.userAgent) || '';
    if (/FBAN|FBAV|Instagram/.test(ua)) return 'iab';
    if (/Safari/.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|EdgA|OPiOS|Android/.test(ua)) return 'safari';
    return 'other';
  }

  /* The steps Clarity hears as custom events, by name and nothing else. The upload
     step stays masked in recordings (privacy policy), so without these a session
     replay shows a customer vanish into a grey box with no way to tell a refused
     photo from a closed tab. */
  // Plan 77 F5: Clarity's funnel reads these names, shared with the race page
  // (`race_picked`, `buy_clicked`), so one funnel spans both doors.
  var CLARITY_STEPS = {
    file_selected: 'upload_started',
    preview_success: 'preview_ok',
    preview_failure: 'preview_failed',
    example_preview: 'example_opened'
  };

  function clarityEvent(name) {
    if (typeof window.clarity !== 'function') return;
    try {
      window.clarity('event', name);
    } catch (err) {
      /* telemetry must never break the funnel */
    }
  }

  function track(name, payload) {
    var detail = sanitize(payload);
    detail.browser = browserKind();
    if (Object.prototype.hasOwnProperty.call(CLARITY_STEPS, name)) clarityEvent(CLARITY_STEPS[name]);
    try {
      document.dispatchEvent(new CustomEvent('traceform:' + name, { detail: detail }));
    } catch (err) {
      /* telemetry must never break the funnel */
    }
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      var row = { event: 'traceform_' + name };
      for (var k in detail) {
        if (Object.prototype.hasOwnProperty.call(detail, k)) row[k] = detail[k];
      }
      window.dataLayer.push(row);
    }
    // The only sink a Shopify custom pixel can hear. Measured 2026-08-26 and again
    // 2026-09-01: on this storefront `fbq`, `_fbq` and `dataLayer` are all undefined,
    // because the store's pixel runs in the Web Pixels sandbox and installs nothing
    // into the parent page. Without this call `traceform_preview_success` -- the
    // event the paused ad set optimises on -- has no route off the page at all.
    if (
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    ) {
      try {
        window.Shopify.analytics.publish('traceform_' + name, detail);
      } catch (err) {
        /* telemetry must never break the funnel */
      }
    }
  }

  /* `preview_success` is the step the round-1 ad set bids on, so it means "a run
     was uploaded and came back as a preview" -- once per file (plan 61 T2). Every
     render reports `preview_rendered`; a customer trying three finishes is one
     customiser, not three. Keyed on the File object, set before the hash resolves,
     so a re-render landing mid-hash cannot report the same file twice. */
  var reportedFile = null;

  /* The event id is the first 64 bits of the file's SHA-256: the same run uploaded
     again after a reload is the same id, which is what lets Meta fold it into one
     event. A hash names the upload and carries none of its content. */
  function reportPreview(file, detail) {
    var send = function (id) {
      if (id) detail.event_id = id;
      track('preview_success', detail);
    };
    try {
      file
        .arrayBuffer()
        .then(function (bytes) {
          return window.crypto.subtle.digest('SHA-256', bytes);
        })
        .then(function (digest) {
          var hex = '';
          var view = new Uint8Array(digest);
          for (var i = 0; i < 8; i++) hex += (view[i] < 16 ? '0' : '') + view[i].toString(16);
          send('tf-pv-' + hex);
        })
        .catch(function () {
          send(null);
        });
    } catch (err) {
      // No subtle crypto (never on an https storefront): the pixel falls back to
      // Shopify's own event id rather than the event being lost.
      send(null);
    }
  }

  function selectedStyle() {
    var checked = root.querySelector('input[name="traceform-style"]:checked');
    return checked ? checked.value : 'paper-ink';
  }

  function selectedAudience() {
    var checked = root.querySelector('input[name="traceform-audience"]:checked');
    return checked && checked.value === 'gift' ? 'gift' : 'self';
  }

  function show(el, msg) {
    if (!el) return;
    if (msg != null) el.textContent = msg;
    el.hidden = false;
  }
  function hide(el) {
    if (el) el.hidden = true;
  }

  /* Plan 56 T25. The named step the render is on, written at the moment the code
     reaches it. Nothing here waits: every call sits immediately before an `await`
     the render had to do anyway, so the line is painted during real work and
     disappears the moment the work is done. There is deliberately no minimum
     display time — a fast render flashing three lines is honest, and a floor
     would be the "labor illusion" wearing a progress bar.

     `stepProgress(null)` clears it. Called from the success, failure and
     supersede paths alike, because a line saying "Placing your stats" left over
     a finished or abandoned render is a claim about work nobody is doing.

     The TEXT is emptied, never the node. Hiding the node meant the line APPEARED
     when the render started, which is 29 px of page arriving under the thumb of
     a customer who is waiting and most likely to tap (measured against
     e2e/funnel-state.spec.ts). base.css reserves the line instead. */
  function stepProgress(msg) {
    if (!progress) return;
    progress.textContent = msg == null ? '' : msg;
  }

  /* The number in the customer's own file, counted after the health-data strip so
     it matches the bytes actually sent. Not an estimate and not the engine's
     opinion: a count of `<trkpt` elements, which is what a GPX calls a recorded
     point. Formatted the way the rest of the page formats numbers (en-US), since
     round 1 is a single-locale US store. */
  function trackpointCount(gpxText) {
    var m = gpxText.match(/<trkpt[\s>]/g);
    return m ? m.length : 0;
  }

  // Switch the pre-upload demo render. Preferred path: the stage holds every
  // colorway and CSS reveals the one named by data-style. Fallback: a single
  // <img> whose src is swapped (explicit data-demo-src, else rewrite the asset
  // filename in place so the CDN query string survives).
  function showDemo(input, style) {
    if (demoStage) {
      demoStage.setAttribute('data-style', style);
      return;
    }
    if (!demoImg) return;
    var explicit = input.getAttribute('data-demo-src');
    if (explicit) {
      demoImg.src = explicit;
      return;
    }
    var current = demoImg.getAttribute('src') || '';
    var next = current.replace(/tf-demo-[a-z-]+\.png/, 'tf-demo-' + style + '.png');
    if (next !== current) demoImg.src = next;
  }

  function selectedVariant() {
    var checked = root.querySelector('input[name="traceform-variant"]:checked');
    return variantsByKey[checked ? checked.value : 'print'] || variantsByKey.print;
  }

  /* What was chosen, in the theme's own type. The native control renders its
     filename in the browser's locale next to an OS button, which is why it is
     transparent now — so this is the only confirmation the customer gets that
     the right run is loaded. */
  function showFileName() {
    if (!fileName) return;
    var chosen = currentFile();
    fileName.textContent = chosen ? chosen.name : '';
    fileName.hidden = !chosen;
    if (dropzone) dropzone.classList.toggle('has-file', Boolean(chosen));
  }

  var captionField = $('.caption-field');
  var captionLabel = $('label[for="traceform-caption"]');
  var captionHelper = $('#traceform-caption-helper');
  var captionReadSeq = 0;

  async function updateCaptionForFile() {
    var seq = ++captionReadSeq;
    var chosen = currentFile();
    var part = null;
    try {
      if (chosen) part = await partFor(chosen);
    } catch (dead) {
      // The action-time read reports file_gone on the upload control.
    }
    if (seq !== captionReadSeq || chosen !== currentFile()) return;
    if (!captionField || !captionLabel || !captionHelper) return;
    var image = part === 'image';
    captionField.classList.toggle('caption-field--image', image);
    captionLabel.textContent = image ? (selectedAudience() === 'gift' ? copy.captionImageLabelGift : copy.captionImageLabel) : copy.captionLabel;
    captionHelper.textContent = image ? (selectedAudience() === 'gift' ? copy.captionImageHelperGift : copy.captionImageHelper) : copy.captionHelper;
  }

  // Run once at start, not only on `change`: WebKit restores the field AND its
  // File when the customer comes back from checkout (measured, plan T4) without
  // firing `change`, and a zone reading "Choose a file" over a file that is
  // already selected is the same dead end wearing a nicer control.
  showFileName();
  updateCaptionForFile();

  /* A different file makes the sealed entitlement wrong, and nothing on the page
     said so: the previous render stayed on screen with its buy control live, so a
     customer who noticed they had uploaded the wrong run and corrected it could
     order the run they had just replaced. The token is sealed to the route it was
     rendered from, so the only safe state after a swap is "no entitlement yet".

     The sequence is bumped as well as aborted. An in-flight render for the old
     file can already be past its `fetch` and reading its blob, and landing would
     re-enable the buy control against the file the customer has just discarded. */
  /* Nothing may be bought against a render that no longer matches what is on the
     page, and an in-flight render for the old inputs must not be allowed to land
     and re-enable the buy control. Everything below this line is state, not
     layout -- the dock is deliberately left alone, because this also runs while
     the customer is typing a dedication and a bar that unmounts per keystroke is
     worse than a disabled one. */
  function invalidateEntitlement() {
    hide(retryBtn);
    token = null;
    try { sessionStorage.removeItem('tf-checkout-preview'); } catch (err) {}
    addBtn.disabled = true;
    renderSeq++;
    if (inFlight) {
      inFlight.abort();
      inFlight = null;
    }
    inFlightSignature = null;
    setAddToCartBusy(false);
    /* A keystroke aborts the render in flight, and the aborted render is by then
       superseded — so its own `finally` deliberately leaves the step line alone
       (a newer render usually owns it). Here there IS no newer render, so the
       line would sit there naming work that was cancelled. */
    stepProgress(null);
  }

  function discardEntitlement() {
    invalidateEntitlement();
    // Tuning stays available after its first reveal, so text edits must still
    // invalidate an automatic render of a replacement file.
    /* The one place `is-unlocked` IS removed, and the visual review pass is what
       found it. `buildPreview` deliberately keeps the mobile dock mounted through
       a re-render, because unmounting it mid-tap was F-002. A file swap is not a
       re-render: it is a deliberate step back up the funnel, and leaving the dock
       up parked a DEAD buy button under the customer's thumb with the explanation
       scrolled off the bottom of the phone. Removing it returns the page to the
       shape it had before the first preview while the replacement file renders
       automatically (plan 57 T5). The shift is
       excluded from CLS anyway: it follows a file selection within 500 ms. */
    addBtn.classList.remove('is-unlocked');
  }

  // T7 enters through the same selection path as the picker and drop handler:
  // filename, image/GPX labels and sanitization must not grow a second version.
  /* Plan 61 T7. Our example run, shown where the customer's own preview lands:
     the pre-rendered demo in the chosen finish, no engine call and no
     entitlement, so there is nothing to buy -- Buy gives way to "Now try yours".
     Its event is `example_preview` and never `preview_success`: the pixel
     forwards only the latter, and a click on our example is not an upload. */
  /* Plan 61 T8. The optional words are one toggle; its state outlives a reload
     for the tab, and a gift choice opens it (the message field is why a giver
     came). Storage is optional: without it the toggle simply starts closed. */
  var ownWords = testid('own-words');
  var OWN_WORDS_KEY = 'tf_own_words_open';
  if (ownWords) {
    try {
      if (window.sessionStorage.getItem(OWN_WORDS_KEY) === '1') ownWords.open = true;
    } catch (err) { /* Storage is optional. */ }
    ownWords.addEventListener('toggle', function () {
      try { window.sessionStorage.setItem(OWN_WORDS_KEY, ownWords.open ? '1' : '0'); } catch (err) { /* Storage is optional. */ }
    });
  }

  var exampleLabel = testid('example-label');
  var tryYours = testid('try-yours');
  var exampleShown = false;

  function exampleSrc() {
    var art = root.querySelector('.demo-blueprint-art--' + selectedStyle());
    return art ? art.currentSrc || art.src : '';
  }

  function showExample() {
    exampleShown = true;
    showReadout(null);
    previewImg.src = exampleSrc();
    show(previewBox);
    hide(previewHint);
    show(exampleLabel);
    addBtn.hidden = true;
    show(tryYours);
    track('example_preview', { style: selectedStyle() });
    // Same landing as a render (plan 64 T4): the whole sheet, "Now try yours" under it.
    previewBox.focus({ preventScroll: true });
    previewBox.scrollIntoView({ block: 'start' });
  }

  function leaveExample() {
    if (!exampleShown) return;
    exampleShown = false;
    hide(exampleLabel);
    hide(tryYours);
    addBtn.hidden = false;
    hide(previewBox);
    show(previewHint);
  }

  var exampleLink = testid('example-link');
  if (exampleLink) exampleLink.addEventListener('click', showExample);
  if (tryYours) {
    tryYours.addEventListener('click', function () {
      if (dropzone) dropzone.scrollIntoView({ block: 'center' });
      fileInput.click();
    });
  }

  /* Plan 72 T3. Keeps `file` with the current choices for a reload. Its day counts
     from the first render of that file: a re-render or a restore never extends it. */
  function keep(file) {
    if (!window.TraceformHandoff) return;
    if (kept.file !== file) kept = { file: file, since: Date.now() };
    window.TraceformHandoff.remember(file, {
      style: selectedStyle(), variant: selectedVariant().key, audience: selectedAudience(),
    }, kept.since);
  }

  function fileChanged(source) {
    hide(welcomeBack);
    // Any run the customer picks replaces the one kept for a reload; the new one
    // is kept once it renders. A restore is the kept run itself.
    if (source !== 'restore' && window.TraceformHandoff) window.TraceformHandoff.forget();
    statedNumbers = null;
    if (cardNumbers) { cardNumbers.reset(); hide(cardNumbers); }
    showReadout(null);
    resetRescue();
    leaveExample();
    hide(errUpload);
    hide(errPreview);
    hide(errCaption);
    setNoteRoom(false);
    var hadPreview = Boolean(token) || !previewBox.hidden;
    discardEntitlement();
    showFileName();
    updateCaptionForFile();
    var chosen = currentFile();
    if (!chosen && uploadStep) uploadStep.classList.remove('has-preview');
    // An in-app browser (Instagram, Facebook) can hand back an empty pick when
    // the photo picker fails to convert the image; silence there reads as broken.
    if (!chosen && source === 'picker') show(errUpload, copy.emptyPick || 'Nothing came through. Pick the image again, or save your Strava screenshot to Photos first.');
    syncFinishLater();
    syncPreviewEmail();
    // Says why the buy control just went dead, on the line that already explains
    // this box. Without it the customer is looking at someone else's render over
    // a disabled button with no instruction — the dead end F-001 was about.
    if (chosen && hadPreview) show(previewHint, copy.previewStale || 'That’s a different run. Your new preview is on its way.');
    if (chosen) {
      track('file_selected', { size_bucket: sizeBucket(chosen.size), style: selectedStyle(), source: source });
      buildPreview(true);
    }
  }
  fileInput.addEventListener('change', function () {
    fileChanged('picker');
  });

  /* Drag and drop. A file dropped onto the transparent input is handled natively
     by the browser, but only for a trusted drop on the input itself — the zone's
     padding, its prompt and its filename line are not it. Assigning the dropped
     FileList makes the selection real (it is the same object the picker would
     have produced); `change` does not fire for a programmatic assignment, so the
     funnel is told about it here. */
  if (dropzone) {
    ['dragenter', 'dragover'].forEach(function (type) {
      dropzone.addEventListener(type, function (event) {
        event.preventDefault();
        dropzone.classList.add('is-dragover');
      });
    });
    dropzone.addEventListener('dragleave', function (event) {
      if (!dropzone.contains(event.relatedTarget)) dropzone.classList.remove('is-dragover');
    });
    dropzone.addEventListener('drop', function (event) {
      event.preventDefault();
      dropzone.classList.remove('is-dragover');
      var dropped = event.dataTransfer;
      if (!dropped || !dropped.files || !dropped.files.length) return;
      fileInput.files = dropped.files;
      fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  if (gpxHelp) {
    gpxHelp.addEventListener('toggle', function () {
      if (gpxHelp.open) track('help_opened', { source: 'gpx_help' });
    });
  }

  /* Plan 23 T10/T18. The backend bounds how many uploads may be in flight and
     answers 429 *without draining the request body* — deliberately, since
     draining it is the cost the cap exists to avoid. A browser whose upload is
     still being written when that response arrives sees a connection reset
     instead of the status code (measured: 4 of 60 concurrent 2.4 MB uploads).
     Both outcomes mean "the server is busy, come back in a moment", so both must
     be retried rather than shown as a failure. Rendering "We couldn't build your
     preview" there loses a customer the backend explicitly asked to return.

     A `fetch` rejection is indistinguishable from offline at this layer, and the
     handling is the same either way: wait, try once more, and only then say
     something went wrong. 503 is included because that is what a restarting
     container answers behind Caddy. */
  var RETRYABLE_STATUS = { 429: 1, 502: 1, 503: 1, 504: 1 };

  function isRetryable(err) {
    if (!err) return false;
    // A rejected `fetch` is a TypeError in every browser (reset, offline, DNS) —
    // that is the connection-reset case above. Errors we throw ourselves are
    // plain Errors carrying our own code, and only the busy statuses are worth
    // repeating: `no_entitlement` is a server contract violation that would fail
    // identically on a second try, so retrying it only delays the honest message.
    if (err instanceof TypeError) return true;
    var m = /^http_(\d{3})$/.exec(err.message || '');
    return Boolean(m && RETRYABLE_STATUS[m[1]]);
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /* The engine says exactly what is wrong and the customer used to be told
     "please try again" for all of it. That is advice that cannot work for 400,
     413 or 422 — the same file fails identically forever — so the customer
     cannot tell "your file has no GPS points" from "we are broken", and leaves.
     At $60-90 cold CAC per upload this is the most expensive sentence on the
     site. Mapped by status, never by echoing the engine's `detail`: that string
     is written for an operator and can name internals. A busy server and a
     broken one still get different words, because only the first is worth
     waiting out.

     Mapped by SOURCE as well as status, because the same status means different
     things to the two intakes and the GPX wording is nonsense for a photo: a 422
     told a customer who sent a stats-only share card that we "couldn't find a GPS
     route in that file… a treadmill activity has nothing to trace", and a 413
     told them to "export a single activity rather than a full history". The image
     map is consulted first and falls back to the GPX one, so a status only needs
     its own image wording where the advice actually differs. */
  function previewMessage(err, part) {
    if (part === 'image' && err && err.message === 'card_numbers') return err.customerMessage;
    if (isRetryable(err)) return copy.previewBusy || 'Our renderer is busy right now. Give it a few seconds, then try again.';
    var message = (err && err.message) || '';
    var bySource = part === 'image' ? copy.previewErrorsImage : null;
    /* Before the card and status maps, and deliberately not keyed by source: the
       refusal is about the FINISH, so the same words serve a photo and a .gpx. */
    if (message === 'ground_unavailable' && copy.previewErrors && copy.previewErrors.ground) {
      return copy.previewErrors.ground;
    }
    /* A card rejection is a 422 with a reason, so it takes its own wording when
       there is one and the plain 422 wording when there is not. That fallback is
       the point: if the engine rewords its prefix this degrades to the route
       message — the previous behaviour — instead of dropping to the generic. */
    var card = /^card_(\w+)$/.exec(message);
    if (card && bySource && bySource[card[1]]) return bySource[card[1]];
    var m = /^http_(\d{3})$/.exec(message);
    /* A file the browser refused to read never reached the engine, so it carries no
       status — but the fact the customer needs is the same one a 422 states: there
       is no usable route in that file, re-export it. It keeps its own telemetry
       reason rather than borrowing `http_422`, because the two mean different
       things to us: this one says the strip failed on the device. */
    var status = card || message === 'gpx_unreadable' ? '422' : m && m[1];
    var known =
      status &&
      ((bySource && bySource[status]) || (copy.previewErrors && copy.previewErrors[status]));
    return known || copy.previewGeneric || 'We couldn’t build your preview. Please try again.';
  }

  /* The engine answers 422 for two unrelated things: a route it cannot read, and
     a dedication its guard refused (`title rejected: <reason>`, engine/api.py
     `_guarded_title`). Classified by status alone, a customer who typed a race
     name was told their FILE was unreadable and sent off to re-export a GPX that
     was never the problem.

     The body is read ONLY to classify. The `detail` itself stays server-side for
     the same reason every other rejection's does: it is written for an operator
     and names internals. If the engine ever rewords the prefix this degrades to
     the route message — the previous behaviour — rather than breaking, so
     tests/api/test_image_intake.py pins the prefix as the tripwire. */
  function captionKind(detail) {
    if (detail.indexOf('title rejected') !== 0) return null;
    if (detail.indexOf('cannot set') !== -1) return 'glyph';
    if (detail.indexOf('contains') !== -1) return 'mark';
    if (detail.indexOf('too long') !== -1) return 'long';
    return 'generic';
  }

  /* The image intake's 422 is two refusals wearing one status: a card whose numbers
     could not be read, and no route on the image. The route wording was serving both,
     so a customer whose card was legible but whose photo was tilted got advice about
     a map that was plainly there — unactionable, so they re-upload the same file and
     leave, at $60-90 an upload.

     There used to be a third, `no_pace`, which sent the customer off to their GPX
     export because the sheet needed all three numbers. It does not any more (plan 26
     T1): a card stating elevation gain where the pace goes now returns a preview with
     one fewer stat. That was the LARGEST single bounce class measured
     (docs/engine/share-card-robustness.md), so the branch is gone rather than
     reworded — a class that no longer bounces needs no message.

     Classified here, like `captionKind`, by the prefix `engine/pipeline.py` puts on it
     (`card_unreadable_reason`), with tests/api/test_image_intake.py as the tripwire if
     it is ever reworded. */
  function cardKind(detail) {
    if (detail.indexOf('card unreadable') === 0) return 'unreadable';
    return detail.indexOf('no route found') === 0 ? 'no_route' : null;
  }

  /* The streets finish, refused because no ground came back (plan 49 R15). Keyed by
     CAUSE and not by source: both intakes can meet it and the advice is the same for
     both, which is why it is not one of the card kinds above. The engine cannot tell a
     map-service outage from a route that will never have a ground — a bbox too wide,
     a window with no main road in it — so the message names the finish change as the
     edit and offers a retry only as the second option. Falls back to the plain 422
     wording if the engine rewords its detail, the same degradation as every other
     classifier here. */
  function groundKind(detail) {
    return detail.indexOf('no streets available') === 0;
  }

  /* Plan 76 T2. `X-Traceform-Card` is the engine's JSON of the numbers a photo's
     sheet was drawn from: distance and unit as the card states them, time and pace
     in seconds (pace per that unit, null when the card states none). Anything that
     does not parse into that shape shows no row rather than a wrong one. */
  function cardNumbersOf(header) {
    var n;
    try {
      n = JSON.parse(header || 'null');
    } catch (err) {
      return null;
    }
    if (!n || typeof n.distance !== 'number' || !(n.distance > 0) || !(n.time_s > 0)) return null;
    if (n.unit !== 'mi' && n.unit !== 'km') return null;
    return n;
  }

  // Seconds as the form takes them: H:MM:SS past an hour, M:SS under it.
  function clock(s) {
    s = Math.round(s);
    var h = Math.floor(s / 3600);
    var m = Math.floor((s % 3600) / 60);
    var ss = String(s % 60).padStart(2, '0');
    return h ? h + ':' + String(m).padStart(2, '0') + ':' + ss : m + ':' + ss;
  }

  function showReadout(n) {
    readNumbers = n;
    if (!cardReadout) return;
    if (!n) return hide(cardReadout);
    // No-break spaces keep a number with its unit when the line wraps on a phone.
    // A time stated without seconds reads as the sheet prints it (geo.fmt_duration):
    // "1h 05m" or "45min", never a 1:05:00 claiming seconds nobody stated.
    var time = clock(n.time_s);
    if (n.time_resolution_s >= 60) {
      var h = Math.floor(n.time_s / 3600);
      var m = Math.floor((n.time_s % 3600) / 60);
      time = h
        ? h + 'h ' + String(m).padStart(2, '0') + 'm'
        : m + 'min';
    }
    var factor = n.unit === 'km' ? 1.609344 : 1;
    var parts = [(n.distance / factor).toFixed(2) + '\u00a0mi', time];
    if (n.pace_s > 0) parts.push(clock(Math.round(n.pace_s * factor)) + '\u00a0/mi');
    var label = n.source === 'typed' ? copy.cardReadoutTyped || 'Your numbers:' : (selectedAudience() === 'gift' ? copy.cardReadoutReadGift : copy.cardReadoutRead) || 'Read from your card:';
    testid('card-readout-numbers').textContent = label + ' ' + parts.join(' · ');
    show(cardReadout);
  }

  function noteKind(detail) {
    var note = /^note rejected: ([a-z_]+)(?::|$)/.exec(detail);
    return note ? note[1] : null;
  }

  /* Same shape as `noteKind`, off the prefix `api._guarded_story` writes. Its own
     prefix is the whole reason that guard is a separate function server-side: a
     refusal reported on the headline focuses a control the customer got right and
     leaves the one they got wrong untouched. */
  function storyKind(detail) {
    var story = /^story rejected: ([a-z_]+)(?::|$)/.exec(detail);
    return story ? story[1] : null;
  }

  async function rejectionOf(res) {
    if (res.status !== 422) return new Error('http_' + res.status);
    var detail = '';
    try {
      var body = await res.json();
      detail = (body && body.detail) || '';
    } catch (err) {
      /* not JSON — it is whatever the route rejection would have been */
    }
    if (typeof detail === 'string' && detail.indexOf('card numbers: ') === 0) {
      var numbersError = new Error('card_numbers');
      numbersError.customerMessage = detail.slice('card numbers: '.length);
      return numbersError;
    }
    var note = noteKind(String(detail));
    if (note) return new Error('note_' + note);
    var story = storyKind(String(detail));
    if (story) return new Error('story_' + story);
    var kind = captionKind(String(detail));
    if (kind) return new Error('caption_' + kind);
    if (groundKind(String(detail))) return new Error('ground_unavailable');
    var card = cardKind(String(detail));
    return new Error(card ? 'card_' + card : 'http_422');
  }

  function captionMessage(kind) {
    var mapped = copy.captionErrors || {};
    if (kind === 'long' && selectedAudience() === 'gift') return copy.captionLongGift;
    return mapped[kind] || mapped.generic || 'We can’t print that line as written. Try saying it another way.';
  }

  function storyMessage(kind) {
    var mapped = copy.storyErrors || {};
    return mapped[kind] || mapped.generic || copy.previewGeneric;
  }

  function noteMessage(kind) {
    var mapped = copy.noteErrors || {};
    return mapped[kind] || copy.previewGeneric;
  }

  function currentCaption() {
    return captionInput ? captionInput.value : '';
  }

  function currentStory() {
    return storyInput ? storyInput.value : '';
  }

  function currentNote() {
    return noteField && !noteField.hidden && noteInput ? noteInput.value : '';
  }

  function setNoteRoom(hasRoom) {
    if (!noteField) return;
    if (hasRoom) {
      show(noteField);
      return;
    }
    if (noteInput) noteInput.value = '';
    hide(errNote);
    hide(noteField);
  }

  async function buildPreview(focusOnDone) {
    hide(errPreview);
    hide(retryBtn);
    hide(errCaption);
    hide(errStory);
    hide(errNote);
    var file = currentFile();
    var numbers = statedNumbers;
    if (!file) {
      show(errUpload, copy.noUpload || 'Choose your run first – your Strava share card, or a .gpx export.');
      fileInput.focus();
      return;
    }
    previewAttempted = true;
    var part;
    hide(errUpload);
    // Identical to what is already running? Then it is the same render arriving
    // twice — duplicate change events or a double-tapped retry —
    // and issuing it would abort a request that is already most of the way there.
    // Joined on NUL because it is the one character a file name or a dedication
    // cannot hold, so no two distinct field sets can collide into one signature.
    // Written as the escape, not as the byte: the byte itself shipped in this
    // asset for one commit and made the file read as binary to every text tool
    // that scans past git's first 8 KB.
    var signature = [file.name, file.size, currentCaption(), currentStory(), currentNote(), selectedStyle(), selectedVariant().sku, JSON.stringify(numbers)].join('\u0000');
    if (inFlight && signature === inFlightSignature) return;
    var startedAt = Date.now();
    // Any in-flight or prior entitlement is for the previously shown finish;
    // invalidate the cart until this render (for the selected finish) lands, so
    // an order can never carry a colorway the customer did not just see.
    token = null;
    inFlightSignature = signature;
    var seq = ++renderSeq;
    if (inFlight) inFlight.abort();
    var controller = new AbortController();
    inFlight = controller;
    function superseded() {
      return seq !== renderSeq;
    }
    addBtn.disabled = true;
    setAddToCartBusy(true);
    var retried = false;
    try {
      /* Set BEFORE the read, not after it. `partFor` is the first await in the
         render and the only thing standing between selection and it is synchronous
         DOM work — so a line written after the await would be replaced by the next
         step inside the same task and never paint at all. (Measured: a
         MutationObserver saw `['', 'Drawing…', '']` with the write on the far side
         of it.) The file type is not known yet, which is why this line does not
         name one. */
      stepProgress(copy.progressReading || 'Reading your file…');
      // Establish render ownership before the asynchronous read, so a late read
      // cannot submit an old file or unlock a superseded variant.
      part = await partFor(file);
      if (superseded()) return;
      if (!part) {
        setAddToCartBusy(false);
        show(errUpload, copy.wrongFileType || 'Send a JPG or PNG of your Strava share card, or a .gpx file.');
        fileInput.focus();
        return;
      }
      /* Health data never goes on the wire. Heart rate, cadence and power all live
         inside <extensions> on a watch's trackpoints, and receiving them is already
         processing under GDPR Art 9 — the controller is a Czech s.r.o., so Art 3(1)
         applies however American the customer is. Stripping them server-side would
         be a delete after the fact, which is why the strip happens here and
         `engine/gpx_sanitize.py` is only the backstop.

         Fail closed on a missing asset. If `traceform-gpx-sanitize.js` 404s or a
         cache serves a version without it, the upload stops with the same message a
         corrupt file gets. A storefront that quietly posted raw watch data because
         a script tag failed is the precise outcome this exists to prevent, so the
         safe direction is a visible error rather than a silent send.

         Share-card images skip it: a photo is not an XML document and the
         engine reads it with a different intake entirely. */
      var payload = file;
      var points = 0;
      if (part === 'gpx') {
        var sanitizer = window.TraceformGpxSanitize;
        if (!sanitizer) throw new Error('gpx_unreadable');
        /* A restored file can be a corpse. Coming back from checkout on WebKit,
           the field and its File both return — but the File is `size: 0` and
           reading it throws `NotFoundError: The object can not be found here.`
           (measured, plan 43 T3). The bytes are gone; only the name survived.

           This is not new breakage. Before the strip existed the dead File was
           appended to the FormData and posted anyway, and the engine answers
           `400 empty upload` for a zero-byte GPX — so the highest-intent path in
           the funnel, the abandoned-checkout return, failed at the server with a
           generic message. The e2e mock returned 200 to any body, which is why it
           looked green for as long as it did.

           So it is caught here and named, and the customer is asked for the file
           again on the field itself, which is the one action that works. */
        var text;
        try {
          text = await file.text();
        } catch (dead) {
          throw new Error('file_gone');
        }
        if (!text) throw new Error('file_gone');
        var cleaned = sanitizer.sanitizeGpxText(text);
        points = trackpointCount(cleaned);
        // Read and stripped before the first attempt, not inside `buildForm`: the
        // retry must not re-read a file the customer may have already replaced, and
        // one strip per render is enough.
        payload = new Blob([cleaned], { type: file.type || 'application/gpx+xml' });
        if (superseded()) return;
      }
      // Rebuilt per attempt: a FormData carrying a File is consumed by the
      // request, so reusing the same object on the retry sends an empty body.
      function buildForm() {
        var form = new FormData();
        // Three-argument append so the stripped Blob still arrives under the
        // customer's own filename; a two-argument append would name it "blob".
        form.append(part, payload, file.name);
        if (part === 'image' && numbers) {
          form.append('card_distance', numbers.distance);
          form.append('card_unit', numbers.unit);
          form.append('card_time', numbers.time);
          // Optional: when sent, the engine holds all three to the card's triangle.
          form.append('card_pace', numbers.pace || '');
        }
        // The dedication the store has always advertised. Sent as typed, empty
        // included: the engine headlines an untitled route with the distance the
        // customer's own file states, so a blank field is a finished product and
        // never a reason to invent words for someone's wall. `race_name` still
        // has no control and is still not sent.
        form.append('title', currentCaption());
        form.append('story', currentStory());
        form.append('note', currentNote());
        form.append('units', 'mi');
        form.append('variant', selectedVariant().sku);
        form.append('style', selectedStyle());
        return form;
      }
      async function attempt() {
        var r = await fetch(window.tfInternalUrl ? window.tfInternalUrl(cfg.previewUrl) : cfg.previewUrl, {
          method: 'POST',
          body: buildForm(),
          signal: controller.signal,
        });
        if (!r.ok) {
          var rejection = await rejectionOf(r);
          rejection.status = r.status;
          throw rejection;
        }
        return r;
      }
      /* Second step, set before the request goes out — the engine's own render is
         the longest part of the wait (measured 1.8-7.7 s), so this is the line the
         customer reads for most of it. A share-card photo has no trackpoints to
         count, so it gets the sentence without a number rather than "0". */
      stepProgress(
        points
          ? (copy.progressDrawingPoints || 'Drawing your route from {count} trackpoints…').replace(
              '{count}',
              points.toLocaleString('en-US'),
            )
          : copy.progressDrawing || 'Drawing your route…',
      );
      var res;
      try {
        res = await attempt();
      } catch (first) {
        if (superseded()) return;
        if (!isRetryable(first)) throw first;
        // One retry, not a loop: the cap is there to shed load, so a client that
        // hammers it is the problem it was built to stop. Long enough for a slot
        // to free at the measured 1.8-7.7 s render times, short enough that the
        // customer reads it as slow rather than broken.
        retried = true;
        track('preview_retry', { style: selectedStyle(), reason: (first && first.message) || 'network' });
        await wait(1500);
        if (superseded()) return;
        res = await attempt();
      }
      if (superseded()) return;
      var entitlement = res.headers.get('X-Traceform-Entitlement');
      if (!entitlement) throw new Error('no_entitlement');
      /* Third step, and the shortest — the headers are in, the image body is still
         streaming. Written immediately before the `await` that drains it, so it
         covers real transfer time and adds none: this is the last line before the
         preview replaces it. */
      stepProgress(copy.progressStats || 'Placing your stats…');
      var blob = await res.blob();
      // Last guard before any shared state is touched, and the reason the token
      // is held in a local until here: everything below lands in one synchronous
      // block, so a newer render can never interleave halfway through it.
      if (superseded()) return;
      token = entitlement;
      previewBlob = blob;
      previewAt = Date.now();
      checkoutRestored = false;
      hide(cardNumbers);
      var card = part === 'image' ? cardNumbersOf(res.headers.get('X-Traceform-Card')) : null;
      /* Plan 76 T6. Numbers the vision model read are kept and sent with every later
         render of this photo, exactly as typed ones are: a finish or format change
         then never asks the model again (a paid call that can fail on a card the
         customer was just shown). Sent as the server stated them, they pass its
         typed-number check exactly as the model's answer did -- one parser for both.
         The server answers those renders as typed; the row still says read. */
      if (card && card.source === 'vision' && !numbers) {
        statedNumbers = {
          distance: String(card.distance),
          unit: card.unit,
          time: clock(card.time_s),
          pace: clock(card.pace_s),
          vision: true
        };
      }
      if (card && card.source === 'typed' && numbers && numbers.vision) card.source = 'vision';
      showReadout(card);
      resetRescue();
      syncFinishLater();
      setNoteRoom(res.headers.get('X-Traceform-Note-Room') === 'yes');
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = URL.createObjectURL(blob); // blob: preview, never a server path
      // The customer's own render never lands under the "Example" label with Buy hidden
      // (example opened after an upload, then a finish or format change re-rendered).
      leaveExample();
      previewImg.src = previewUrl;
      show(previewBox);
      hide(previewHint);
      addBtn.disabled = buying;
      show(tuningPanel);
      setAddToCartBusy(false);
      // Set once and never removed: the docked mobile bar hangs off this class,
      // and a later render that is busy or fails must not take the customer's
      // purchase control off the screen it is already on.
      addBtn.classList.add('is-unlocked');
      hasPreviewed = true;
      /* Plan 72 T3. Kept on the device only after a render succeeded, so a reload
         never brings back a refused photo under "here's your preview". A render
         that needed typed card numbers is not kept: the numbers are not stored,
         and the photo alone would be refused again. One the vision model read is
         kept on every render, as a read one is: the model reads it again. */
      if (!numbers || numbers.vision) keep(file);
      if (file === restoredFile) { show(welcomeBack); revealRestoredPreview(); }
      previewedFile = file;
      syncPreviewEmail();
      var done = { style: selectedStyle(), ms: Date.now() - startedAt };
      track('preview_rendered', done);
      if (reportedFile !== file) {
        reportedFile = file;
        reportPreview(file, done);
      }
      /* Plan 64 T4. The upload area folds to one "Change file" line and the
         page lands with the preview's top under the header, so the whole print
         sits above the Buy dock on a phone (`focus()` alone left half of it
         under the dock). */
      if (uploadStep) uploadStep.classList.add('has-preview');
      if (focusOnDone !== false) {
        previewBox.focus({ preventScroll: true });
        previewBox.scrollIntoView({ block: 'start' });
      }
    } catch (err) {
      // A superseded render has nothing to report: its failure is not the
      // customer's problem, and its message would overwrite the newer render's.
      if (superseded()) return;
      /* Every image refusal offers a handmade preview immediately.
         Superseded requests cannot reveal the rescue form. */
      if (part === 'image' && err && err.status === 422) {
        syncFinishLater();
        if (err.message === 'card_unreadable' || err.message === 'card_numbers') show(cardNumbers);
        testid('rescue-file').textContent = file.name;
        show(rescue);
      }
      // The button stays disabled — there is no entitlement — but it must stop
      // claiming to be working on something that has already failed.
      setAddToCartBusy(false);
      // the reason is our own error code, never the browser's message text
      track('preview_failure', {
        style: selectedStyle(),
        ms: Date.now() - startedAt,
        reason: (err && err.message) || 'unknown',
        retried: retried,
      });
      // A refused dedication is a defect in one field, not in the upload. It is
      // reported on that field so the customer edits the thing that was actually
      // rejected — the file they just chose is still good.
      var note = /^note_(\w+)$/.exec((err && err.message) || '');
      var story = /^story_(\w+)$/.exec((err && err.message) || '');
      var caption = /^caption_(\w+)$/.exec((err && err.message) || '');
      // A refused field inside the folded own-words group cannot take focus.
      if ((note || story || caption) && ownWords) ownWords.open = true;
      if (note) {
        show(errNote, noteMessage(note[1]));
        if (noteInput) noteInput.focus();
      } else if (story) {
        show(errStory, storyMessage(story[1]));
        if (storyInput) storyInput.focus();
      } else if (caption) {
        show(errCaption, captionMessage(caption[1]));
        if (captionInput) captionInput.focus();
      } else if (err && err.message === 'file_gone') {
        // Reported on the FIELD, not on the preview: the fix is to pick the file
        // again, and an error next to the image points at the wrong
        // control. Same reason a refused dedication is reported on the caption.
        hide(errPreview);
        show(errUpload, copy.fileGone || copy.noUpload || 'Choose your run again – the file your browser kept is no longer readable.');
        fileInput.focus();
      } else {
        show(errPreview, previewMessage(err, part));
        // Every failure keeps a way back. With "Create my preview" gone (plan 57
        // T5) a render starts only on `change`, and choosing the same file again
        // does not fire it – without this a 500 left no control that retries.
        if (!cardNumbers || cardNumbers.hidden) show(retryBtn);
      }
    } finally {
      // Only the newest render owns the controls. A superseded one clearing the
      // busy state would tell the customer the render finished while the render
      // they are waiting for is still running.
      if (!superseded()) {
        if (inFlight === controller) {
          inFlight = null;
          inFlightSignature = null;
        }
        // Same ownership rule as the controls above: only the newest render clears
        // the step line, so a superseded one cannot blank the line belonging to the
        // render the customer is actually waiting for.
        stepProgress(null);
      }
    }
  }

  retryBtn.addEventListener('click', function () {
    buildPreview(true);
  });

  if (cardNumbers) cardNumbers.addEventListener('submit', function (event) {
    event.preventDefault();
    if (!cardNumbers.reportValidity()) return;
    statedNumbers = {
      distance: testid('card-distance').value,
      unit: testid('card-unit').value,
      time: testid('card-time').value.trim(),
      pace: testid('card-pace').value.trim()
    };
    buildPreview(true);
  });

  /* Plan 76 T2. Opens the same form a refused card gets, filled with the numbers
     this sheet was drawn from, so fixing one number means changing one field. */
  if (cardReadout) testid('card-fix').addEventListener('click', function () {
    var n = readNumbers;
    if (!n || !cardNumbers) return;
    testid('card-distance').value = n.distance.toFixed(2);
    testid('card-unit').value = n.unit;
    testid('card-time').value = clock(n.time_s);
    testid('card-pace').value = n.pace_s > 0 ? clock(n.pace_s) : '';
    hide(cardReadout);
    show(cardNumbers);
    testid('card-distance').focus();
  });

  function resetRescue() {
    rescueGeneration += 1;
    if (!rescue) return;
    hide(rescue);
    rescue.reset();
    testid('rescue-status').textContent = '';
    testid('rescue-file').textContent = '';
    testid('rescue-submit').disabled = false;
  }

  if (rescue) rescue.addEventListener('submit', async function (event) {
    event.preventDefault();
    var submit = testid('rescue-submit');
    var file = currentFile();
    if (rescue.hidden || submit.disabled || !file || !testid('rescue-consent').checked || !rescue.reportValidity()) return;
    var generation = rescueGeneration;
    var status = testid('rescue-status');
    var variant = root.querySelector('input[name="traceform-variant"]:checked');
    var form = new FormData();
    form.append('image', file, file.name);
    form.append('email', testid('rescue-email').value.trim());
    form.append('consent', 'true');
    form.append('format', variant && variant.value === 'print' ? 'print' : 'framed');
    form.append('handle', cfg.productHandle);
    submit.disabled = true;
    status.textContent = copy.rescueBusy;
    try {
      var response = await fetch(cfg.rescueUrl, { method: 'POST', body: form });
      var message = copy.rescueError;
      if (response.status === 422) {
        var body = await response.json();
        if (body.detail === 'consent is required' || body.detail === 'email address not valid') message = body.detail;
      }
      if (generation !== rescueGeneration) return;
      status.textContent = response.status === 202 ? copy.rescueSent : message;
      submit.disabled = response.status === 202;
    } catch (err) {
      if (generation !== rescueGeneration) return;
      status.textContent = copy.rescueError;
      submit.disabled = false;
    }
  });

  // Changing the finish re-renders in that colorway (keeps the shown preview and
  // the sealed entitlement in sync). Before a first upload it swaps the demo
  // render instead, so the colorways are judgeable without uploading anything.
  Array.prototype.forEach.call(styleInputs, function (input) {
    input.addEventListener('change', function () {
      var style = selectedStyle();
      if (checkoutRestored && !currentFile()) { invalidateEntitlement(); show(errUpload, copy.previewRestoreError); }
      var chosen = currentFile();
      track('demo_finish_changed', { style: style, source: chosen ? 'preview' : 'demo' });
      if (chosen) {
        buildPreview(false);
      } else {
        showDemo(input, style);
        if (exampleShown) previewImg.src = exampleSrc();
      }
    });
  });

  // The entitlement is sealed to the variant it was rendered for. Switching
  // print<->framed after a preview must re-render so the token re-seals to the
  // new variant — otherwise the cart would carry a print token on a framed line
  // and the order is rejected as WRONG_VARIANT and never fulfilled. Before a
  // first upload the variant only changes the price, so there is nothing to sync.
  // The lower cards label radios above the preview. Native label activation
  // scrolls to those radios; keep keyboard focus without moving the viewport.
  root.querySelectorAll('.variant-card[for]').forEach(function (label) {
    label.addEventListener('click', function (event) {
      var input = document.getElementById(label.htmlFor);
      if (!input || input.disabled) return;
      event.preventDefault();
      input.focus({ preventScroll: true });
      if (input.checked) return;
      var scrollX = window.scrollX;
      var scrollY = window.scrollY;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      // Changing the mat also changes layout; flush it before restoring position.
      window.scrollTo(scrollX, scrollY);
    });
  });
  Array.prototype.forEach.call(variantInputs, function (input) {
    input.addEventListener('change', function () {
      setAddToCartBusy(false);
      if (currentFile()) buildPreview(false);
      else if (checkoutRestored) { invalidateEntitlement(); show(errUpload, copy.previewRestoreError); }
    });
  });

  /* Audience is not artwork: it never joins `buildForm` and never spends a
     render. Plan 64 T2: choosing Gift no longer switches the format to framed
     (plan 56 T24 did) – the price jumped by $80 under a choice about who the
     print is for. The gift ad starts on framed instead, visibly, in the switch. */
  Array.prototype.forEach.call(audienceInputs, function (input) {
    input.addEventListener('change', function (event) {
      var audience = selectedAudience();
      if (!event.detail || !event.detail.traceformLanding) track('audience_changed', { audience: audience });
      if (audience === 'gift' && ownWords) ownWords.open = true;
      syncFinishLater();
      if (audience === 'gift' && !(event.detail && event.detail.traceformLanding)) revealFinishLater();
      // Plan 72 T3: no render follows, so the kept run takes the new audience here.
      if (kept.file && kept.file === currentFile()) keep(kept.file);
    });
  });

  /* The dedication is printed on the sheet and is sealed into the entitlement by
     the render that carried it — so editing it after a successful preview left
     the customer reading their new words on the page above a token still carrying
     the old ones, with the buy control live. It is the same rule the finish and
     the format already follow: change what gets printed, re-render.

     `change`, not `input`: it fires once the field is left and only if the value
     actually moved, so a dedication costs one render rather than one per
     keystroke. */
  if (captionInput) {
    /* Typing is enough to make the shown render stale, and blur is too late to be
       the only guard: a render issued for the old words can land while the field
       already shows the new ones and re-enable the buy control against text the
       customer has moved away from. So the keystroke invalidates and the blur
       re-renders. No render is issued per keystroke. */
    captionInput.addEventListener('input', function () {
      if (hasPreviewed) invalidateEntitlement();
    });
    /* `change`, not `input`: it fires once the field is left and only if the value
       actually moved, so a dedication costs one render rather than one per
       keystroke. Gated on `hasPreviewed` because before a first preview there is
       nothing out of sync — tabbing out of an optional field the customer was
       merely filling in must not upload their run and spend a render slot. */
    captionInput.addEventListener('change', function () {
      if (currentFile() && hasPreviewed) buildPreview(false);
    });
  }

  /* The customer's own sentence prints too, so it follows the same rule as the
     dedication above it, verbatim: the keystroke marks the shown render stale and
     leaving the field re-renders.

     Not one render per keystroke, and that is a limit rather than a taste: the
     engine allows ten previews a minute per customer (`config.Limits`), and a
     sentence typed with the ordinary pauses would spend most of them and then
     meet a 429 mid-thought. Leaving the field is the one moment that means "this
     is what I want it to say". */
  if (storyInput) {
    storyInput.addEventListener('input', function () {
      if (hasPreviewed) invalidateEntitlement();
    });
    storyInput.addEventListener('change', function () {
      if (currentFile() && hasPreviewed) buildPreview(false);
    });
  }

  if (noteInput) {
    noteInput.addEventListener('input', function () {
      if (hasPreviewed) invalidateEntitlement();
    });
    noteInput.addEventListener('change', function () {
      if (currentFile() && hasPreviewed) buildPreview(false);
    });
  }

  /* Plan 57 T5, owner 2026-09-21: one Buy replaces the cart summary and
     separate checkout action for the fewest steps to buy; approval = payment.
     The buyer judges the visible preview and finish controls, so no second
     thumbnail, label summary or post-payment approval reminder is needed. */
  addBtn.addEventListener('click', async function () {
    // Both docked controls once put two primary actions under the same thumb;
    // a second tap posted a second /cart/add: measured, two lines in the cart.
    // Lock before awaiting; each Buy replaces earlier Traceform lines.
    if (buying) return;
    hide(errCart);
    if (!token) {
      show(errCart, copy.previewRequired);
      return;
    }
    clarityEvent('buy_clicked');
    var variant = selectedVariant();
    var withDigital = !!(digital && digitalBox && digitalBox.checked);
    var style = selectedStyle();
    var items = [{ id: variant.id, quantity: 1, properties: { _entitlement: token } }];
    if (copyAddon && copyBox && copyBox.checked) items.push({ id: copyAddon.id, quantity: 1, properties: { _entitlement: token } });
    if (withDigital) items.push({ id: digital.id, quantity: 1, properties: { _entitlement: token } });
    buying = true;
    addBtn.disabled = true;
    setAddToCartBusy(true);
    // Freeze the design during the cart round trip so the purchased token and
    // selected controls cannot diverge while a response is on its way.
    var controls = root.querySelectorAll('input, textarea');
    Array.prototype.forEach.call(controls, function (input) { input.disabled = true; });
    try {
      {
        var added = await window.TraceformBuy.replace(items, (cfg.variants || []).map(function (v) { return v.id; }).concat(digital ? [digital.id] : [], copyAddon ? [copyAddon.id] : []));
        if (window.TraceformCartThumb) {
          window.TraceformCartThumb.keep(added && added.items, window.TraceformCartThumb.capture(previewImg));
        }
        // `audience` is the two-valued flag, which is the most a gift may
        // contribute to a funnel metric.
        track('add_to_cart', {
          audience: selectedAudience(), variant: variant.key, style: style, digital: withDigital,
        });
      }
      await rememberCheckout();
      track('checkout_started', { variant: variant.key, style: style });
      // Plan 72 T3. The run is in the cart now; a later visit starts empty rather
      // than offering the same print again. Awaited, because the navigation below
      // can cut a transaction short; `forget` gives up after two seconds.
      if (window.TraceformHandoff) await window.TraceformHandoff.forget();
      // Hand off to the real Shopify checkout. On the local contract fixture this
      // same-origin path is served a stub.
      window.location.assign(cfg.checkoutUrl);
      navigating = true;
    } catch (err) {
      show(errCart, copy.cartError);
    } finally {
      if (!navigating) resetBuy();
    }
  });

  function resetBuy() {
    buying = false;
    navigating = false;
    Array.prototype.forEach.call(root.querySelectorAll('input, textarea'), function (input) { input.disabled = false; });
    // Not an unconditional re-enable. A file swap or caption edit that discards
    // the entitlement must not leave a live Buy button whose handler can only
    // ask for a preview. This also guards a restored page after checkout.
    addBtn.disabled = !token;
    setAddToCartBusy(false);
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted && history.state && history.state.traceformCheckout) {
      addBtn.disabled = true;
      window.location.reload();
      return;
    }
    if (history.state && history.state.traceformCheckout) return;
    if (event.persisted) resetBuy();
    // Some browsers restore the form after deferred scripts have already run.
    if ((event.persisted || !previewAttempted) && !token && !inFlight && currentFile()) buildPreview(false);
  });
  if (copyBox) copyBox.addEventListener('change', function () { setAddToCartBusy(false); });
  if (digitalBox) digitalBox.addEventListener('change', function () { setAddToCartBusy(false); });

  /* The runtime Buy price must match Liquid's `money` filter. Runtime totals
     used to be written as `'$' + (total/100).toFixed(0)`, yielding `$188` three
     lines under variant cards saying `$169.00` — the same offer in two formats,
     in the one place the customer checks what they just agreed to.
     Round 1 is a single-currency US store (config/round1.json), so the format is
     pinned to the one `| money_without_trailing_zeros` produces there rather than
     parsed out of it: `$119`, and cents only when there are some (plan 81 N5). */
  function money(cents) {
    var digits = cents % 100 ? 2 : 0;
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits
    });
  }

  /* Plan 61 T14. A giver rarely holds the recipient's run, so a gift without a
     file offers one email with a link back here. Only the address, the format,
     the product and the ad concept travel -- never a file -- and only with the
     consent box ticked; the backend refuses anything else. The answer is the
     same whether or not this address asked before, so the status line only ever
     says "on its way" or "try again". */
  function syncFinishLater() {
    updateCaptionForFile();
    root.querySelectorAll('[data-audience-copy]').forEach(function (node) {
      node.textContent = node.getAttribute('data-' + selectedAudience());
    });
    if (errCaption && !errCaption.hidden && (errCaption.textContent === copy.captionErrors.long || errCaption.textContent === copy.captionLongGift)) errCaption.textContent = captionMessage('long');
    showReadout(readNumbers);
    var transcript = root.querySelector('[data-testid="howto-upload-step"]');
    if (transcript) transcript.textContent = selectedAudience() === 'gift' ? 'Upload their Strava share card or GPX file' : 'Upload your Strava share card or GPX file';
    var uploadStep = root.querySelector('#traceform-upload-step');
    if (uploadStep) uploadStep.setAttribute('aria-label', selectedAudience() === 'gift' ? 'Upload their run' : 'Upload your run');
    var steps = root.querySelector('[data-testid="card-steps"] img');
    if (steps) {
      var gift = selectedAudience() === 'gift';
      steps.src = steps.getAttribute(gift ? 'data-gift-src' : 'data-self-src');
      steps.alt = steps.alt.replace(gift ? 'your run' : 'their run', gift ? 'their run' : 'your run');
    }
    var giftWithoutRun = selectedAudience() === 'gift' && !currentFile();
    if (giftDoor) giftDoor.hidden = !giftWithoutRun;
    if (finishLater) finishLater.hidden = !giftWithoutRun;
  }
  /* Plan 64 T3. On a phone the form starts at the bottom edge of the first
     screen; tapping Gift, or into the form, brings all of it into view so the
     address, the consent and the send button need no hunting. */
  function revealFinishLater() {
    if (finishLater && !finishLater.hidden) finishLater.scrollIntoView({ block: 'nearest' });
  }
  /* Plan 74 T3. Offered only for the file whose preview has landed: before any
     preview it would be an email ask in front of the product (bible ch. 9 §6), and
     after a new file it would promise a preview that does not exist yet. A
     colourway or format change re-renders the same file and keeps the offer. */
  function syncPreviewEmail() {
    var ready = checkoutRestored || Boolean(previewedFile && previewedFile === currentFile());
    if (!previewEmail) return;
    previewEmail.hidden = !ready;
  }

  /* One sender for both forms (plan 61 T14 gift link, plan 74 preview link). The
     body is built here, never from the form, so nothing but these fields can travel. */
  function wireFinishLater(form, audience) {
    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      var status = form.querySelector('.finish-later-status');
      var submit = form.querySelector('.finish-later-submit');
      var variant = root.querySelector('input[name="traceform-variant"]:checked');
      var creative = '';
      try { creative = window.sessionStorage.getItem('tf_creative') || ''; } catch (err) { /* Storage is optional. */ }
      var body = {
        email: form.querySelector('input[name="email"]').value.trim(),
        audience: audience(),
        format: variant && variant.value === 'print' ? 'print' : 'framed',
        handle: cfg.productHandle,
        creative: /^[A-Za-z0-9-]{1,64}$/.test(creative) ? creative : '',
        consent: form.querySelector('input[name="consent"]').checked === true
      };
      if (body.audience === 'preview') {
        body.colourway = selectedStyle();
        if (token) body.entitlement = token;
      }
      submit.disabled = true;
      status.textContent = copy.finishLaterBusy;
      try {
        var r = await fetch(cfg.finishLaterUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (r.status !== 202) throw new Error('finish-later ' + r.status);
        status.textContent = copy.finishLaterSent;
      } catch (err) {
        status.textContent = copy.finishLaterError;
        submit.disabled = false;
      }
    });
  }
  if (finishLater) {
    finishLater.addEventListener('focusin', revealFinishLater);
    wireFinishLater(finishLater, function () { return selectedAudience() === 'self' ? 'self' : 'gift'; });
  }
  if (previewEmail) wireFinishLater(testid('preview-email-form'), function () { return 'preview'; });

  if (window.TraceformLanding) window.TraceformLanding.apply(root, copy);
  syncFinishLater();
  setAddToCartBusy(false);
  if (!(history.state && history.state.traceformCheckout) && currentFile() && !previewAttempted) buildPreview(false);

  function putFile(file) {
    try {
      var transfer = new DataTransfer();
      transfer.items.add(file);
      fileInput.files = transfer.files;
      return true;
    } catch (err) {
      return false;
    }
  }

  // A stored value only ever selects a control that exists; it never builds a selector.
  function check(inputs, value) {
    Array.prototype.forEach.call(inputs, function (input) {
      if (input.value === value) input.checked = true;
    });
  }

  /* Plan 72 T3. The run kept by the last successful render, with the choices it
     rendered with. The finish-later email's explicit colourway, format and
     audience in the URL still win (plan 70 T2): those are the customer's latest word. */
  function restoreRun() {
    window.TraceformHandoff.recall().then(function (entry) {
      if (!entry || currentFile() || previewAttempted) return;
      var query = new URLSearchParams(window.location.search);
      if (!query.get('tf_colourway')) check(styleInputs, entry.choices.style);
      if (!query.get('tf_format')) check(variantInputs, entry.choices.variant);
      if (!query.get('tf_audience')) check(audienceInputs, entry.choices.audience);
      if (!putFile(entry.file)) return;
      restoredFile = currentFile();
      kept = { file: restoredFile, since: entry.timestamp };
      fileChanged('restore');
    });
  }

  async function rememberCheckout() {
    try {
      var image = await new Promise(function (resolve, reject) {
        if (!previewBlob) return resolve(previewImg.src);
        var reader = new FileReader();
        reader.onload = function () { resolve(reader.result); };
        reader.onerror = reject;
        reader.readAsDataURL(previewBlob);
      });
      sessionStorage.setItem('tf-checkout-preview', JSON.stringify({
        at: previewAt, token: token, image: image, style: selectedStyle(),
        variant: selectedVariant().key, audience: selectedAudience(),
        caption: captionInput && captionInput.value, story: storyInput && storyInput.value,
        note: noteInput && noteInput.value, numbers: readNumbers
      }));
    } catch (err) { /* Back still shows a recovery line if storage is unavailable. */ }
    try { history.replaceState(Object.assign({}, history.state, { traceformCheckout: true }), ''); } catch (err) { /* optional */ }
  }

  // Wait until page restoration and image layout have settled; never smooth-scroll.
  var restoredPreviewRevealed = false;
  function revealRestoredPreview() {
    if (restoredPreviewRevealed) return;
    restoredPreviewRevealed = true;
    function reveal() {
      requestAnimationFrame(function () { requestAnimationFrame(function () {
        var rect = previewBox.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
          previewBox.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      }); });
    }
    if (document.readyState === 'complete') reveal();
    else window.addEventListener('pageshow', reveal, { once: true });
  }

  async function restoreEmailedPreview() {
    var restoreToken;
    try {
      restoreToken = sessionStorage.getItem('tf-restore');
      sessionStorage.removeItem('tf-restore');
    } catch (err) { return false; }
    if (!restoreToken) return false;
    // An emailed preview takes priority over this tab's older checkout/run.
    invalidateEntitlement();
    var restoringSeq = renderSeq;
    var failure = copy.previewRestoreError;
    try {
      var res = await fetch(cfg.finishLaterRestoreUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: restoreToken })
      });
      if (res.status === 410) failure = copy.savedPreviewExpired;
      if (res.status !== 200) throw new Error('restore_failed');
      var entitlement = res.headers.get('X-Traceform-Entitlement');
      var saved = JSON.parse(res.headers.get('X-Traceform-Restored'));
      var variant = (cfg.variants || []).find(function (v) { return saved && v.sku === saved.variant; });
      if (!entitlement || !variant || !Array.prototype.some.call(styleInputs, function (input) {
        return input.value === saved.style;
      })) throw new Error('restore_invalid');
      var blob = await res.blob();
      // Landing reapplies URL radios on pageshow; sealed choices must land last.
      if (document.readyState !== 'complete') {
        await new Promise(function (resolve) { window.addEventListener('pageshow', resolve, { once: true }); });
      }
      if (renderSeq !== restoringSeq) return true;
      fileInput.value = '';
      check(styleInputs, saved.style); check(variantInputs, variant.key);
      if (captionInput) captionInput.value = saved.title || '';
      if (storyInput) storyInput.value = saved.story || '';
      if (noteInput) noteInput.value = saved.note || '';
      previewBlob = blob;
      previewAt = Date.now();
      checkoutRestored = true;
      hasPreviewed = previewAttempted = true;
      previewImg.onload = function () {
        previewImg.onload = null;
        if (renderSeq !== restoringSeq) return;
        token = entitlement;
        resetBuy();
        revealRestoredPreview();
      };
      previewImg.onerror = function () {
        if (renderSeq !== restoringSeq) return;
        invalidateEntitlement(); hide(previewBox); show(errUpload, copy.previewRestoreError);
      };
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = URL.createObjectURL(blob);
      previewImg.src = previewUrl;
      syncPreviewEmail(); syncFinishLater();
      show(previewBox); hide(previewHint); show(tuningPanel); show(welcomeBack);
      if (uploadStep) uploadStep.classList.add('has-preview');
      addBtn.classList.add('is-unlocked'); resetBuy();
    } catch (err) {
      if (renderSeq === restoringSeq) show(errUpload, failure);
    }
    return true;
  }

  async function restoreCheckout() {
    if (!history.state || !history.state.traceformCheckout) {
      try { sessionStorage.removeItem('tf-checkout-preview'); } catch (err) { /* optional */ }
      return false;
    }
    try {
      var saved = JSON.parse(sessionStorage.getItem('tf-checkout-preview'));
      if (!saved || !Number.isFinite(saved.at) || Date.now() - saved.at < 0 || Date.now() - saved.at >= 24 * 60 * 60 * 1000 ||
          !saved.token || !/^data:image\//.test(saved.image) || !variantsByKey[saved.variant]) throw new Error('missing');
      var restoringSeq = renderSeq;
      if (!await window.TraceformBuy.hasEntitlement(saved.token)) {
        sessionStorage.removeItem('tf-checkout-preview');
        fileInput.value = '';
        history.replaceState(Object.assign({}, history.state, { traceformCheckout: false }), '');
        return true; // A completed checkout is a fresh page, not a recovery failure.
      }
      if (renderSeq !== restoringSeq) return true;
      check(styleInputs, saved.style); check(variantInputs, saved.variant); check(audienceInputs, saved.audience);
      if (captionInput) captionInput.value = saved.caption || '';
      if (storyInput) storyInput.value = saved.story || '';
      if (noteInput) noteInput.value = saved.note || '';
      previewAt = saved.at;
      checkoutRestored = true;
      syncPreviewEmail();
      hasPreviewed = previewAttempted = true;
      previewImg.onload = function () {
        previewImg.onload = null;
        if (renderSeq !== restoringSeq) return;
        token = saved.token;
        resetBuy();
        revealRestoredPreview();
      };
      previewImg.onerror = function () { invalidateEntitlement(); hide(previewBox); show(errUpload, copy.previewRestoreError); };
      previewImg.src = saved.image;
      syncFinishLater();
      show(previewBox); hide(previewHint); show(tuningPanel); show(welcomeBack);
      showReadout(saved.numbers);
      if (uploadStep) uploadStep.classList.add('has-preview');
      addBtn.classList.add('is-unlocked'); resetBuy();
      return true;
    } catch (err) {
      show(errUpload, copy.previewRestoreError);
      return false;
    }
  }

  restoreEmailedPreview().then(function (restoredEmail) {
    return restoredEmail || restoreCheckout();
  }).then(function (restoredCheckout) {
    if (window.TraceformHandoff && !restoredCheckout) {
      /* Take even on an ordinary PDP visit so expired entries are removed. Deletion
         commits before the File arrives here. Never replace a file the buyer chose
         while IndexedDB was opening, or a browser-restored selection. DataTransfer
         failure leaves the ordinary picker usable, without a customer-facing error.
         No run from home: the one kept for a reload, if any. */
      window.TraceformHandoff.take().then(function (file) {
        if (currentFile() || previewAttempted) return;
        if (!file) return restoreRun();
        if (putFile(file)) fileChanged('home');
      });
    }
  });
})();
